/**
 * Pure helpers for the snake-markets (ARC-84 Phase G4) player surfaces.
 *
 * Snake markets are short-term crypto **Up/Down** markets. The strike (`price
 * to beat`) lives on `outcome.strikePrice`; **Up** = Over (the Yes side) and
 * **Down** = Under (the No side, i.e. `1 − yes`).
 *
 * Pricing convention mirrors the binary feature:
 * - Seed prices arrive from the API as stringified probabilities in `[0, 1]`.
 * - Live `PriceUpdated` events arrive with `yesPrice` / `noPrice` already in
 *   **cents (1–99)**.
 *
 * Everything downstream works in **cents** (0–100); {@link toCents} (reused
 * from `binary-market`) normalises both sources before display math.
 */
import { toCents, centsToPercent } from '@/lib/binary-market';

/** The two snake sides. `up` = Over (Yes), `down` = Under (No). */
export type SnakeSide = 'up' | 'down';

/** A live overlay for a snake series: the contract prices (cents) + the spot. */
export interface SnakeLive {
  /** Up (Yes) price in cents (0–100), or `null` until the first tick. */
  upCents: number | null;
  /** Down (No) price in cents (0–100), or `null` until the first tick. */
  downCents: number | null;
  /** Latest underlying-asset spot price (dollars), or `null` until the first tick. */
  spot: number | null;
}

/** A single streamed trade dot on the snake chart. */
export interface SnakeTradeDot {
  /** Stable id (the streamed trade id / monotonic counter). */
  id: string;
  /** Unix seconds of the trade. */
  time: number;
  /** Trade price in cents (0–100). */
  cents: number;
  /** The side the trade hit. */
  side: SnakeSide;
}

/**
 * The whole-number Up% (the Up/Yes side's implied probability) for a series,
 * preferring the live overlay over the API seed. `null` when unknown.
 */
export function upPercent(seedYesPrice: string | null | undefined, live: SnakeLive | undefined): number | null {
  if (live && live.upCents != null) return centsToPercent(live.upCents);
  return centsToPercent(toCents(seedYesPrice));
}

/**
 * Format a dollar spot/strike price for display. Crypto spots span many orders
 * of magnitude (BTC ~70k, BONK ~0.00002), so the precision adapts: ≥1 → 2 dp,
 * otherwise up to 8 significant digits. `null` → `—`.
 */
export function formatSpot(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${value.toPrecision(4)}`;
}

/**
 * Parse a strike-price string (from `outcome.strikePrice` or a settled
 * `settledPrice`) to a number, or `null` when missing/unparseable.
 */
export function parsePrice(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Human countdown string for a close time, e.g. `4m 32s`, `1h 05m`, `2d 3h`.
 * `now` is unix-ms (injected so the timer hook drives re-render). Past/`null`
 * close times render `Closed`.
 */
export function formatCountdown(closeTimeIso: string | null | undefined, nowMs: number): string {
  if (!closeTimeIso) return '—';
  const closeMs = new Date(closeTimeIso).getTime();
  if (Number.isNaN(closeMs)) return '—';
  const diffMs = closeMs - nowMs;
  if (diffMs <= 0) return 'Closed';

  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, '0')}m`;
  if (mins > 0) return `${mins}m ${String(secs).padStart(2, '0')}s`;
  return `${secs}s`;
}

/** Returns true when the series' close time is in the past (drives auto-advance). */
export function isClosed(closeTimeIso: string | null | undefined, nowMs: number): boolean {
  if (!closeTimeIso) return false;
  const closeMs = new Date(closeTimeIso).getTime();
  return !Number.isNaN(closeMs) && closeMs <= nowMs;
}

/**
 * Format the market window in **Eastern Time** for the lobby card subtitle,
 * e.g. `Mar 10, 2:55–3:00 PM ET`. The date is shown once (from the open time)
 * and the AM/PM marker once (shared, at the end, taken from the close time) to
 * match the Figma compact format. `null`/unparseable inputs render `—`.
 */
export function formatWindowEt(openIso: string | null | undefined, closeIso: string | null | undefined): string {
  if (!openIso || !closeIso) return '—';
  const open = new Date(openIso);
  const close = new Date(closeIso);
  if (Number.isNaN(open.getTime()) || Number.isNaN(close.getTime())) return '—';

  const TZ = 'America/New_York';
  const timeParts = (d: Date): Record<string, string> =>
    new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true })
      .formatToParts(d)
      .reduce<Record<string, string>>((acc, p) => {
        acc[p.type] = p.value;
        return acc;
      }, {});

  const o = timeParts(open);
  const c = timeParts(close);
  const datePart = new Intl.DateTimeFormat('en-US', { timeZone: TZ, month: 'short', day: 'numeric' }).format(open);
  const meridiem = (c.dayPeriod ?? '').toUpperCase();

  return `${datePart}, ${o.hour}:${o.minute}–${c.hour}:${c.minute} ${meridiem} ET`;
}

/**
 * Format an ISO timestamp as a short **Eastern Time** clock, e.g. `2:50 AM`,
 * for the timeline strip pills. `null`/empty/unparseable inputs render `''`.
 */
export function formatEtClockTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/**
 * The instance duration, in minutes, for a recurring snake sub-category code.
 * Drives the "upcoming" pill on the timeline strip (close + one period).
 * Returns `null` for unknown/missing codes.
 */
export function periodDurationMinutes(subCategoryCode: string | undefined): number | null {
  switch (subCategoryCode) {
    case 'CRYPTO_5MIN':
      return 5;
    case 'CRYPTO_20MIN':
      return 20;
    case 'CRYPTO_2HOURS':
      return 120;
    case 'CRYPTO_DAILY':
      return 1440;
    case 'CRYPTO_WEEKLY':
      return 10080;
    default:
      return null;
  }
}

/**
 * The next window's close time (ISO) for a recurring series, derived as
 * `currentClose + one period`. Snake windows are back-to-back (each window opens
 * exactly when the prior closes), so the next close is known in advance — this
 * lets the timeline strip show the upcoming pill immediately, without waiting
 * for the backend to post the UPCOMING instance. Returns `null` when the close
 * time is missing/unparseable or the period is unknown.
 */
export function nextWindowCloseIso(
  currentCloseIso: string | null | undefined,
  subCategoryCode: string | undefined,
): string | null {
  const minutes = periodDurationMinutes(subCategoryCode);
  if (!currentCloseIso || minutes == null) return null;
  const closeMs = new Date(currentCloseIso).getTime();
  if (Number.isNaN(closeMs)) return null;
  return new Date(closeMs + minutes * 60_000).toISOString();
}

/**
 * Countdown clock for the timer pill, formatted **`HH:MM:SS`** down to the
 * series close. `nowMs` is unix-ms (injected so the 1s ticker drives re-render).
 * Past/`null`/unparseable close times render `00:00:00`.
 */
export function formatClock(closeTimeIso: string | null | undefined, nowMs: number): string {
  if (!closeTimeIso) return '00:00:00';
  const closeMs = new Date(closeTimeIso).getTime();
  if (Number.isNaN(closeMs)) return '00:00:00';

  const totalSec = Math.max(0, Math.floor((closeMs - nowMs) / 1000));
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

/** The recurring period the lobby previews by default (admin can change which
 *  periods are active, ARC-84 §7.2). */
export const SNAKE_LOBBY_DEFAULT_SUBCATEGORY = 'CRYPTO_5MIN';

/**
 * The resolution a lobby card previews by default: the **5-minute** window when
 * available, else the closest active period (periods arrive closest-first).
 * `undefined` when the currency exposes no periods.
 */
export function defaultLobbyPeriod<T extends { subCategoryCode: string }>(periods: readonly T[]): T | undefined {
  return periods.find((p) => p.subCategoryCode === SNAKE_LOBBY_DEFAULT_SUBCATEGORY) ?? periods[0];
}
